import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect } from "react";
import Markdown from "react-markdown";
import markdownTOC from "remark-toc";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useUserGuide } from "../../../hooks/useUserGuide";
import { components } from "../utils";

const staffUserGuidesHTML = `# 🧾 Staff App User Guide

---

## Contents

---

## 🧠 Key Terms

**App Login URL**: [https://app.inhouse.health](https://app.inhouse.health/) — Your portal to the staff web app.

**Open Shifts**: Unassigned shifts that you can voluntarily pick up through the app.

**Recurring Shifts:** Shifts that repeat automatically on a set cadence. Indicated by a sync icon.

**Profile Page:** New layout to manage contact info, view recurring shifts, and roles.

**Chat:** In-app messaging tool for communication with staff.

**Broadcasts:** Messages sent to multiple users by role or employment type.

**Preferences**: Your availability settings that help match you with suitable open shifts and scheduling opportunities.

**Replacement**: When you post a shift you can't work for another staff member to take. You remain responsible until someone fills and confirms the shift.

**Password Reset**: Required for first-time login or when you need to restore access. Available on the main login page.

---

## ⚡ Quick Guide

**Application:** [https://app.inhouse.health](https://app.inhouse.health/)

**Username:** Your personal email address

### 🔹 First-Time Login

**Step 1: Find Your Welcome Email or Text**

- Check for a message from \`support@inhouse.health\` or a text from \`(224) 302-6482\`
- If you can't find it:
    - Look in your spam/junk folders
    - Still no luck? Visit [https://app.inhouse.health](https://app.inhouse.health/), click **"Password Reset"**, and follow the prompts
        - You can reset it via email or text

**Step 2: Access the Setup Link**

- Click the link in your welcome message
- If the link isn't clickable, copy and paste it into your browser

**Step 3: Set Your Password**

- Password must be:
    - At least 8 characters
    - Include one uppercase letter
    - Include one special character (e.g., \`!@#$%^&*\`)

**Step 4: Log In**

- Username: your personal email
- Password: your newly created password

---

## 📅 View Schedule

**Step 1: Login**

- Go to [https://app.inhouse.health](https://app.inhouse.health/) or open the app from your home screen

**Step 2: View Your Schedule**

- Go to the schedule section to see your upcoming shifts

    ![step2.png](/docs/staff-user-guides/step2.png)


---

## 🟢 Pick-Up Open Shifts

**Step 1: Open the Menu**

- Tap the menu icon

    ![Pick up.png](/docs/staff-user-guides/Pick_up.png)


**Step 2: Open the "Open Shifts" Page**

- View available shifts

    ![Pick up_2.png](/docs/staff-user-guides/Pick_up_2.png)


**Step 3: Review Available Shifts**

- Tap any shift to see details and accept it

    ![Pick up_3.png](/docs/staff-user-guides/Pick_up_3.png)


---

## 📈 Update Availability for Additional Shifts

Keep your preferences current to match with shifts that fit your schedule.

**Step 1: Open the Menu**

- Go to the main menu

    ![Update_1.png](/docs/staff-user-guides/Update_1.png)


**Step 2: Select "Preferences"**

![Update_2.png](/docs/staff-user-guides/Update_2.png)

**Step 3: Update Your Availability**

- Set your preferred days and times

    ![Update_3.png](/docs/staff-user-guides/Update_3.png)


**Step 4: Review Preferences**

- Verify all details are correct

    ![Update_4.png](/docs/staff-user-guides/Update_4.png)


---

## 🔁Managing Recurring Shifts

Step 1: View Recurring Shifts

- Recurring shifts have a sync icon

Step 2: Edit or cancel Recurring Shifts

- Select the shift > Select edit/delete
- Confirm your changes

---

## 🔀 Find Replacement for Shift

Post a shift you can't work (except for PTO-covered shifts).

**Step 1: Open the Menu**

- Open the app menu

    ![Replacement_1.png](/docs/staff-user-guides/Replacement_1.png)


**Step 2: Select "Find Replacement"**

![replacement_2.png](/docs/staff-user-guides/replacement_2.png)

**Step 3: Post the Shift**

- Enter details and confirm

    ![Replacement_3.png](/docs/staff-user-guides/Replacement_3.png)


**Step 4: Monitor Status**

- Track whether someone claims your shift. Remember, you're responsible until it's filled and approved.

    ![Replacement_4.png](/docs/staff-user-guides/Replacement_4.png)


---

## ❌ Cancel Replacement Request

![cancel_1.png](/docs/staff-user-guides/cancel_1.png)

![cancel_2.png](/docs/staff-user-guides/cancel_2.png)

If you posted a shift by mistake or your plans change:

- Go to **Find Replacement**
- Tap your posted shift
- Select **Cancel Request**

---

## 📲 Save App to Home Screen

This web-based app isn't in the App Store. Here's how to access it easily:

**Step 1: Log In**

- Visit [https://app.inhouse.health](https://app.inhouse.health/)

**Step 2: Save to Home Screen**

**For Android:**

- Tap the 3-dot menu in your browser
- Select **"Install App"**

**For iPhone:**

![save_1.png](/docs/staff-user-guides/save_1.png)

![save_2.png](/docs/staff-user-guides/save_2.png)

- Tap the **Share** button
- Scroll down and choose **"Add to Home Screen"**`;

export const StaffUserGuides: FC = () => {
	const { userInfo } = useAuth();
	const { track } = useAnalytics();

	const { data, isLoading, isError, error, refetch } = useUserGuide();

	useEffect(() => {
		track("user_guide_opened", {
			user_id: userInfo?.id,
			guide_type: "staff",
			is_admin: false,
			role: userInfo?.role,
		});
	}, [track, userInfo?.id, userInfo?.role]);

	if (isLoading) {
		return (
			<Box
				component="main"
				sx={{
					maxHeight: "100%",
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					padding: 4,
				}}
			>
				<Box sx={{ textAlign: "center" }}>
					<CircularProgress size={48} />
					<Typography variant="body1" sx={{ marginTop: 2 }}>
						Loading user guide...
					</Typography>
				</Box>
			</Box>
		);
	}

	if (isError) {
		return (
			<Box
				component="main"
				sx={{
					maxHeight: "100%",
					width: "100%",
					padding: 4,
				}}
			>
				<Alert severity="error" sx={{ marginBottom: 2 }}>
					<Typography variant="h6" gutterBottom>
						Failed to load user guide
					</Typography>
					<Typography variant="body2">{error instanceof Error ? error.message : "An unknown error occurred"}</Typography>
					<Button variant="outlined" onClick={() => refetch()} sx={{ marginTop: 2 }}>
						Try Again
					</Button>
				</Alert>
			</Box>
		);
	}

	return (
		<Box component="main" sx={{ maxHeight: "100%", width: "100%", overflowY: "auto" }}>
			<Box sx={{ padding: 3 }}>
				<Markdown components={components} remarkPlugins={[markdownTOC]} urlTransform={(url) => url}>
					{data?.content || staffUserGuidesHTML}
				</Markdown>
			</Box>
		</Box>
	);
};
